// System settings schemas — plan §4.7.4 + §13.6 S2.
// Tüm setting key'leri registry'de tanımlı. UI bu registry'yi bilir, BE de aynısını kullanır.

import { z } from "zod";

// Kayıtlı key'ler (dotted notation). Her key için is_secret + description.
export const SettingKey = z.enum([
  // LLM
  "llm.openai.api_key",
  "llm.openai.model",
  "llm.anthropic.api_key",
  "llm.anthropic.model",
  "llm.active_provider",
  // M8-S2 — model fiyat override'ları (JSON) + worker concurrency
  "llm.pricing",
  "llm.concurrency",
  // SMTP
  "mail.smtp.host",
  "mail.smtp.port",
  "mail.smtp.user",
  "mail.smtp.password",
  "mail.smtp.secure",
  "mail.from_name",
  "mail.from_email",
  // Prompt
  "prompt.system",
  // Bütçe
  "budget.monthly_usd_limit",
  "budget.alert_threshold_pct",
  // Developer mode
  "ui.dev_mode_json_panel",
  // M6 — bütçe alert flag (worker set eder, AdminDashboard okur)
  "ui.budget_alert_active",
  // M8-S3 — queue pause flag (E1 auth pause + manuel pause)
  "system.queue_paused",
  "system.queue_paused_reason",
  // M12 — system default branding (JSON value_plain'de saklanır, is_secret=false)
  "branding.default",
  // Geçmiş Rapor Aktarımı
  "report_import.concurrency",
  "report_import.confidence_threshold",
  // Rapor Tutarlılık Denetimi
  "prompt.report_check",
  "report_check.max_input_tokens",
  // TODO-EVIDENCE-02 — kanıt belgesi politikası (sistem geneli v1; müşteri bazlı override devir)
  "evidence.retention_days",
  "evidence.import_retention_days",
  "evidence.default_confidentiality",
]);
export type SettingKey = z.infer<typeof SettingKey>;

export interface SettingRegistryEntry {
  key: SettingKey;
  label: string;
  description: string;
  isSecret: boolean;
  group: "llm" | "mail" | "prompt" | "budget" | "ui" | "evidence";
  // Value tipi (UI form rendering için). Default string.
  type?: "string" | "number" | "boolean";
  // Default (encrypted hale getirilmeden önce)
  default?: string;
}

export const SETTING_REGISTRY: SettingRegistryEntry[] = [
  // LLM
  {
    key: "llm.openai.api_key",
    label: "OpenAI API Key",
    description: "sk-... ile başlar. https://platform.openai.com/api-keys",
    isSecret: true,
    group: "llm",
  },
  {
    key: "llm.openai.model",
    label: "OpenAI Model",
    description: "Örn: gpt-4o, gpt-4-turbo",
    isSecret: false,
    group: "llm",
    default: "gpt-4o",
  },
  {
    key: "llm.anthropic.api_key",
    label: "Anthropic API Key",
    description: "sk-ant-... ile başlar. https://console.anthropic.com/settings/keys",
    isSecret: true,
    group: "llm",
  },
  {
    key: "llm.anthropic.model",
    label: "Anthropic Model",
    description: "Örn: claude-sonnet-5-5, claude-opus-5-5 (eski: claude-sonnet-4-6)",
    isSecret: false,
    group: "llm",
    default: "claude-sonnet-4-6",
  },
  {
    key: "llm.active_provider",
    label: "Aktif LLM Provider",
    description: "openai / anthropic / dummy (dev)",
    isSecret: false,
    group: "llm",
    default: "dummy",
  },
  {
    key: "llm.pricing",
    label: "Model Fiyatları (JSON override)",
    description:
      'JSON: { "openai": { "gpt-4o": { "input_per_m": 2.5, "output_per_m": 10 } } }. ' +
      "Boş bırakırsan kod içindeki default fiyatlar kullanılır. USD / milyon token.",
    isSecret: false,
    group: "llm",
    default: "",
  },
  {
    key: "llm.concurrency",
    label: "Üretim Worker Concurrency",
    description:
      "pg-boss worker'ın aynı anda çalıştıracağı LLM üretim job sayısı (1-10). Yüksek değer hız, düşük değer rate-limit koruması.",
    isSecret: false,
    group: "llm",
    type: "number",
    default: "3",
  },
  // Geçmiş Rapor Aktarımı
  {
    key: "report_import.concurrency",
    label: "Rapor Aktarımı Eşzamanlılığı",
    description: "Aynı anda işlenen rapor sayısı (1–4). Üretim kuyruğundan bağımsızdır.",
    isSecret: false,
    group: "llm",
    type: "number",
    default: "2",
  },
  {
    key: "report_import.confidence_threshold",
    label: "Rapor Aktarımı Güven Eşiği",
    description:
      "Bu değerin altındaki yanıtlar formda 'düşük güven' olarak işaretlenir (0–1). Yazmayı engellemez.",
    isSecret: false,
    group: "llm",
    type: "number",
    // Kalibrasyon 2026-09-26 (26 rapor): kalite eşiği 0,70'te kırılıyor — elle okumada
    // 0,60–0,69 bandının %32–43'ü hatalı, 0,70–0,79 %30, 0,90+ %0. Eşik yazmayı
    // engellemediği için tek maliyet gözden geçirilecek yanıt sayısıdır (%3,6 → %10,2).
    default: "0.70",
  },
  // Rapor Tutarlılık Denetimi
  {
    key: "prompt.report_check",
    label: "Rapor Tutarlılık Denetimi Prompt'u",
    description:
      "Denetim modeline verilen sistem talimatı. Boş bırakırsan kod içindeki varsayılan kullanılır. Özel prompt yazarsan koruma kuralları (veri/talimat ayrımı ve dönem kuralı) sonuna otomatik eklenir.",
    isSecret: false,
    group: "prompt",
    default: "",
  },
  {
    key: "report_check.max_input_tokens",
    label: "Rapor Denetimi Girdi Tavanı (token)",
    description:
      "Tek koşumda modele gönderilecek en fazla girdi token'ı. Modelin bağlam penceresiyle kesişimi alınır.",
    isSecret: false,
    group: "llm",
    type: "number",
    default: "220000",
  },
  // SMTP
  {
    key: "mail.smtp.host",
    label: "SMTP Host",
    description: "Örn: smtp.sendgrid.net, smtp.gmail.com",
    isSecret: false,
    group: "mail",
  },
  {
    key: "mail.smtp.port",
    label: "SMTP Port",
    description: "465 (SSL) veya 587 (STARTTLS)",
    isSecret: false,
    group: "mail",
    type: "number",
    default: "587",
  },
  {
    key: "mail.smtp.user",
    label: "SMTP Kullanıcı Adı",
    description: "API key veya kullanıcı adı",
    isSecret: false,
    group: "mail",
  },
  {
    key: "mail.smtp.password",
    label: "SMTP Şifre",
    description: "SMTP relay için şifre / API key",
    isSecret: true,
    group: "mail",
  },
  {
    key: "mail.smtp.secure",
    label: "TLS/STARTTLS",
    description: "true (465) veya false (587 STARTTLS)",
    isSecret: false,
    group: "mail",
    type: "boolean",
    default: "false",
  },
  {
    key: "mail.from_name",
    label: "Gönderen Ad",
    description: "Mail'lerin 'From' adı",
    isSecret: false,
    group: "mail",
    default: "Mahir AI",
  },
  {
    key: "mail.from_email",
    label: "Gönderen E-posta",
    description: "Mail'lerin 'From' adresi",
    isSecret: false,
    group: "mail",
  },
  // Prompt
  {
    key: "prompt.system",
    label: "Sistem Prompt (Layer 1 — AI 4-katman)",
    description:
      "4 katmanlı prompt'ta Layer 1. Rapor türü ve standart adları BURADA OLMAZ; " +
      "onlar her framework'ün kendi AI bağlamında (Form Yöneticisi → Framework) yaşar. " +
      "Burası sadece evrensel kurallar: dil, uydurma yasağı, biçim, ton.",
    isSecret: false,
    group: "prompt",
    default:
      "Sen kurumsal rapor bölümleri yazan profesyonel bir AI'sın. Her zaman Türkçe, resmi ve açık dilde yaz. Rapor türü (sürdürülebilirlik, faaliyet raporu, TSRS/GRI/BM Global Compact vb.) ve standartlara özgü yapı — aktif framework'ün AI bağlamından gelir; sen framework bağlamına uyarsın.\n\nKRİTİK KURAL — UYDURMA YASAĞI:\n- Kullanıcının sağladığı form yanıtlarında veya bağlam bölümünde bulunmayan hiçbir sayısal veri, tarih, yüzde, isim, iddia veya olay uydurma.\n- Bir bölüm için yeterli veri yoksa, genel/çerçeve bilgiyi sun ve veri eksikliğini nötr ifadelerle ima et; asla rakam, KPI, sertifika, ödül veya denetim sonucu ekleme.\n- Sertifikalar, ödüller, denetim sonuçları, KPI değerleri, yüzdeler yalnızca kullanıcı yanıtlarında açıkça geçiyorsa raporlanır.\n\nBİÇİM KURALLARI:\n- Başlık/bölüm yapısı için framework katmanındaki yönergelere uy.\n- Madde imi, numara, tablo gibi biçim unsurlarını framework gerektiriyorsa kullan; yoksa düz paragraf tercih et.\n- Standart gereksinim kodlarını (GRI 2-7, TSRS S2-14, BFR-3 vb.) anlatı metninin içine yazma; çerçeve katmanı açıkça isterse hariç.\n- Yabancı dilde teknik terimi ilk geçişte Türkçe karşılığıyla parantez içinde ver (örn: 'materiality (önemlilik)').\n- İçeriğe uygun ton: resmi, nesnel, üçüncü tekil; pazarlama dili kullanma.",
  },
  // Bütçe
  {
    key: "budget.monthly_usd_limit",
    label: "Aylık Bütçe (USD)",
    description: "Bu eşiği aşıldığında admin uyarılır",
    isSecret: false,
    group: "budget",
    type: "number",
    default: "100",
  },
  {
    key: "budget.alert_threshold_pct",
    label: "Uyarı Eşiği (%)",
    description: "Bütçenin yüzde kaçına ulaşıldığında uyarı çıksın",
    isSecret: false,
    group: "budget",
    type: "number",
    default: "80",
  },
  // UI
  {
    key: "ui.dev_mode_json_panel",
    label: "Developer Mode — FormManager JSON Panel",
    description: "FormManager'da JSON panel görünsün mü (admin için)",
    isSecret: false,
    group: "ui",
    type: "boolean",
    default: "false",
  },
  {
    key: "ui.budget_alert_active",
    label: "Bütçe Alert Flag",
    description: "M6 worker aylık bütçe eşiği aşılınca set eder. AdminDashboard okur.",
    isSecret: false,
    group: "ui",
    type: "boolean",
    default: "false",
  },
  {
    key: "system.queue_paused",
    label: "Üretim Queue Duraklatıldı",
    description:
      "LLM auth error (E1) veya admin manuel pause. Worker bu flag true iken " +
      "yeni job işlemez; admin 'Devam et' der resume olur.",
    isSecret: false,
    group: "ui",
    type: "boolean",
    default: "false",
  },
  {
    key: "system.queue_paused_reason",
    label: "Queue Pause Nedeni",
    description: "auth (LLM 401/403) | manual | budget. FE banner'da gösterilir.",
    isSecret: false,
    group: "ui",
    default: "",
  },
  // Kanıt belgeleri (TODO-EVIDENCE-02) — /admin/settings/evidence sekmesi
  {
    key: "evidence.retention_days",
    label: "Referanssız kanıt dosyası saklama süresi (gün)",
    description:
      "Hiçbir KPI hücresine bağlı olmayan (bağı kaldırılmış ya da hiç bağlanmamış) kanıt dosyaları " +
      "yüklenmelerinden bu kadar gün sonra depolamadan ve listeden silinir. Bağlı dosyaya dokunulmaz. " +
      "0 = temizlik kapalı. Günlük bakım görevi uygular; audit: evidence.purged.",
    isSecret: false,
    group: "evidence",
    type: "number",
    default: "180",
  },
  {
    key: "evidence.import_retention_days",
    label: "İçe aktarma kaynak dosyası saklama süresi (gün)",
    description:
      "KPI içe aktarmanın kaynak dosyası (Excel/PDF/Word) aktarım tamamlandıktan bu kadar gün sonra " +
      "depolamadan silinir; aktarılan değerler ve hücre bağları (sayfa/hücre konumu) KALIR, dosya " +
      "yalnız indirilemez olur. 0 = temizlik kapalı.",
    isSecret: false,
    group: "evidence",
    type: "number",
    default: "365",
  },
  {
    key: "evidence.default_confidentiality",
    label: "Yeni kanıt dosyası gizlilik varsayılanı",
    description:
      "internal = yalnız danışman görür, portalda müşteriye hiç inmez · client = müşteriye erişim " +
      "politikasıyla açılır · public = (ileride dış paylaşım; şimdilik client gibi). Müşterinin " +
      "kendi yüklediği dosya her zaman client olur.",
    isSecret: false,
    group: "evidence",
    default: "client",
  },
  {
    key: "branding.default",
    label: "Mahir AI Default Brand (JSON)",
    description:
      "Müşteriye özel brand atanmamışsa kullanılan default. JSON: BrandingConfig + logoStoragePath.",
    isSecret: false,
    group: "ui",
    default: "",
  },
];

// API request/response
export const SettingUpdateRequestSchema = z.object({
  key: SettingKey,
  value: z.string(), // plaintext — BE şifreler
});
export type SettingUpdateRequest = z.infer<typeof SettingUpdateRequestSchema>;

// Listing response: secret value'lar maskelenmiş (örn `sk-****abc`), non-secret'lar açık.
export const SettingViewSchema = z.object({
  key: SettingKey,
  value: z.string().nullable(), // masked if secret
  isSecret: z.boolean(),
  hasValue: z.boolean(),
  updatedAt: z.string().datetime().nullable(),
});
export type SettingView = z.infer<typeof SettingViewSchema>;

export const SettingListResponseSchema = z.object({
  settings: z.array(SettingViewSchema),
});
export type SettingListResponse = z.infer<typeof SettingListResponseSchema>;

// Secret maskeleme: "sk-abc123defghi" → "sk-****ghi"
export function maskSecret(value: string): string {
  if (value.length <= 6) return "****";
  const prefix = value.slice(0, 3);
  const suffix = value.slice(-3);
  return `${prefix}****${suffix}`;
}

// TODO-EVIDENCE-02 — Kanıt belgeleri sekmesi durum kartı (GET /admin/settings/evidence-status)
export const EvidencePolicyStatusSchema = z.object({
  scanProvider: z.enum(["mock", "clamav"]),
  scanner: z.object({ ok: z.boolean(), message: z.string().optional() }),
  retention: z.object({ evidenceDays: z.number(), importDays: z.number(), dryRun: z.boolean() }),
  counts: z.object({
    evidencePending: z.number(),
    evidenceInfected: z.number(),
    evidencePurged: z.number(),
    attachmentsPending: z.number(),
    attachmentsInfected: z.number(),
  }),
});
export type EvidencePolicyStatus = z.infer<typeof EvidencePolicyStatusSchema>;

// POST /admin/settings/evidence-purge/preview — dry-run adayları (silmez)
export const EvidencePurgePreviewSchema = z.object({
  scanned: z.number(),
  planned: z.array(
    z.object({
      id: z.string(),
      action: z.enum(["delete_row", "purge_object"]),
      reason: z.string(),
    }),
  ),
});
export type EvidencePurgePreview = z.infer<typeof EvidencePurgePreviewSchema>;

// M5-S3 — Mail test
export const MailTestResponseSchema = z.object({
  success: z.boolean(),
  to: z.string(),
  latencyMs: z.number().optional(),
  errorMessage: z.string().optional(),
});
export type MailTestResponse = z.infer<typeof MailTestResponseSchema>;

// M5-S3 — Audit log entry (read-only)
export const AuditLogEntrySchema = z.object({
  id: z.string(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  metadata: z.unknown().nullable(),
  actorEmail: z.string().nullable(),
  actorName: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>;

export const AuditLogResponseSchema = z.object({
  entries: z.array(AuditLogEntrySchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type AuditLogResponse = z.infer<typeof AuditLogResponseSchema>;

// M8-S2 — audit log query params (filter + search + pagination).
// M9-S3 eklemeler: entityId filter — ProjectDetail audit widget entity-filtered fetch için.
export const AuditLogQuerySchema = z.object({
  search: z.string().trim().min(1).max(200).optional(),
  action: z.string().max(100).optional(),
  entityType: z.string().max(50).optional(),
  entityId: z.string().max(100).optional(),
  actorUserId: z.string().uuid().optional(),
  dateFrom: z.string().datetime().optional(),
  dateTo: z.string().datetime().optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().min(10).max(200).default(50),
});
export type AuditLogQuery = z.infer<typeof AuditLogQuerySchema>;

// M5-S4 — Prompt preview
export const PromptPreviewRequestSchema = z.object({
  projectId: z.string().uuid().optional(),
  sectionCode: z.string().optional(),
  questionCode: z.string().optional(),
});
export type PromptPreviewRequest = z.infer<typeof PromptPreviewRequestSchema>;

export const PromptPreviewResponseSchema = z.object({
  systemLayer: z.string(),
  frameworkLayer: z.string(),
  sectionLayer: z.string(),
  questionLayer: z.string(),
  userResponseBlock: z.string(),
  fullText: z.string(),
  resolvedPlaceholders: z.record(z.string(), z.string()),
  /** TODO-KPI-24: "=== KPI TABLOSU ===" bloğu (projede KPI seti varsa); yoksa null. */
  kpiBlock: z.string().nullable(),
});
export type PromptPreviewResponse = z.infer<typeof PromptPreviewResponseSchema>;

// Placeholder catalog — FormManager'da "kullanılabilir token'lar" listesi için
export const PLACEHOLDER_CATALOG: { token: string; description: string; scope: string }[] = [
  {
    token: "{{company_name}}",
    description: "Müşteri şirket adı",
    scope: "framework / section / question",
  },
  { token: "{{sector}}", description: "Müşteri sektörü", scope: "framework / section / question" },
  {
    token: "{{company_size}}",
    description: "Şirket büyüklüğü (small/medium/large/enterprise)",
    scope: "framework / section / question",
  },
  {
    token: "{{reporting_year}}",
    description: "Raporlama yılı",
    scope: "framework / section / question",
  },
  {
    token: "{{reporting_period}}",
    description: 'Raporlama dönemi (yıllıkta yalnızca yıl, çeyreklikte "YIL · N. Çeyrek")',
    scope: "framework / section / question",
  },
  {
    token: "{{current_date}}",
    description: "Bugünün tarihi (YYYY-MM-DD)",
    scope: "framework / section / question",
  },
  {
    token: "{{section_code}}",
    description: "Aktif bölüm kodu (örn GRI-302)",
    scope: "section / question",
  },
  { token: "{{section_label}}", description: "Aktif bölüm adı", scope: "section / question" },
  { token: "{{question_code}}", description: "Aktif soru kodu", scope: "question" },
  { token: "{{question_label}}", description: "Aktif soru metni", scope: "question" },
  {
    token: "{{previous_year_answer}}",
    description: "Önceki dönem yanıtı (aynı dönem türü, bir önceki yıl)",
    scope: "question",
  },
];

// M5-S4 — AI cost aggregate (aylık)
// M11 — attachment metric'leri eklendi (LLM'e gönderilen ek belge sayım/byte tracking)
export const AIMonthlyCostResponseSchema = z.object({
  monthStart: z.string().datetime(),
  monthlyUsd: z.number(),
  budgetUsd: z.number().nullable(),
  thresholdPct: z.number().nullable(),
  percentUsed: z.number().nullable(),
  callCount: z.number(),
  // KK (Kalite Kontrol) Agent — maliyet ayrımı (generation vs qc). Backend
  // GET /admin/cost/monthly llm_call_log.purpose group-by ile doldurur (Task 13).
  byPurpose: z.object({
    generation: z.number(),
    qc: z.number(),
    // Geçmiş Rapor Aktarımı (regresyon review RG1) — import worker'ının LLM maliyeti ayrı satır.
    reportImport: z.number(),
    // Rapor Tutarlılık Denetimi (spec §4.4 RG1) — llm_call_log.purpose='report_check'.
    reportCheck: z.number(),
    // KPI paket AI önerisi — llm_call_log.purpose='kpi_suggest'.
    kpiSuggest: z.number(),
  }),
  // M11 — aylık aggregate attachment kullanımı
  attachmentCallCount: z.number(),
  totalAttachments: z.number(),
  totalAttachmentBytes: z.number(),
  providerBreakdown: z.array(
    z.object({
      provider: z.string(),
      calls: z.number(),
      costUsd: z.number(),
      // M11 — provider başına attachment metric'leri
      attachmentCalls: z.number(),
      totalAttachments: z.number(),
      totalAttachmentBytes: z.number(),
    }),
  ),
});
export type AIMonthlyCostResponse = z.infer<typeof AIMonthlyCostResponseSchema>;

// M5-S2 — LLM test response
export const LLMTestResponseSchema = z.object({
  success: z.boolean(),
  provider: z.string(),
  model: z.string(),
  latencyMs: z.number().optional(),
  errorCode: z
    .enum(["auth", "rate_limit", "content_filter", "malformed", "timeout", "network", "unknown"])
    .optional(),
  errorMessage: z.string().optional(),
});
export type LLMTestResponse = z.infer<typeof LLMTestResponseSchema>;
